import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, ArrowLeft, Download, User, Eye, Share2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Header } from '../Header';
import { Footer } from '../Footer';
import { blogAPI } from '../../services/api';
import { formatDateTime } from '../../lib/date';

const ShareButtons = ({ url, title }) => {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const networks = [
    {
      name: 'WhatsApp',
      href: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
      bg: 'bg-[#25D366] hover:bg-[#1ebe57]',
      icon: (
        <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-1.607zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.612-.916-2.207-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.017-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>
      ),
    },
    {
      name: 'Facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      bg: 'bg-[#1877F2] hover:bg-[#0e63d3]',
      icon: (
        <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
      ),
    },
    {
      name: 'LinkedIn',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      bg: 'bg-[#0A66C2] hover:bg-[#08518f]',
      icon: (
        <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
      ),
    },
  ];

  return (
    <div className="flex items-center gap-3 flex-wrap" data-testid="share-buttons">
      <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
        <Share2 className="h-4 w-4" /> Partager :
      </span>
      {networks.map((n) => (
        <a
          key={n.name}
          href={n.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Partager sur ${n.name}`}
          data-testid={`share-${n.name.toLowerCase()}`}
          className={`inline-flex items-center justify-center h-10 w-10 rounded-full transition-colors ${n.bg}`}
        >
          {n.icon}
        </a>
      ))}
    </div>
  );
};

export const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchedSlug = useRef(null);

  useEffect(() => {
    if (fetchedSlug.current === slug) return;
    fetchedSlug.current = slug;
    fetchPost();
    window.scrollTo(0, 0);
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const data = await blogAPI.getPostBySlug(slug);
      setPost(data);

      const allPosts = await blogAPI.getPosts();
      setRelatedPosts(allPosts.filter((p) => p.id !== data.id).slice(0, 3));
    } catch (error) {
      console.error('Error fetching post:', error);
      setPost(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div>
        <Header />
        <div className="min-h-screen flex items-center justify-center mt-20">
          <p>Chargement...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div>
        <Header />
        <div className="min-h-screen flex items-center justify-center mt-20">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Article non trouvé</h1>
            <Button onClick={() => navigate('/actualites')} className="bg-green-600 hover:bg-green-700 text-white">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour aux actualités
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <div>
      <Helmet>
        <title>{post.title} | Actualités AGIPLUS CI</title>
        <meta name="description" content={post.excerpt} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:type" content="article" />
        {post.image && <meta property="og:image" content={post.image} />}
        <link rel="canonical" href={currentUrl} />
      </Helmet>
      <Header />

      <article className="py-24 bg-gray-50 mt-24">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Button
              onClick={() => navigate('/actualites')}
              variant="outline"
              className="mb-6"
              data-testid="back-to-blog"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour aux actualités
            </Button>

            <Card className="bg-white">
              {post.image && (
                <div className="relative h-96 overflow-hidden rounded-t-lg">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
                </div>
              )}

              <CardContent className="p-8">
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{post.title}</h1>

                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-600 mb-6 pb-6 border-b border-gray-100">
                  <span className="flex items-center gap-1.5" data-testid="article-published-at">
                    <Calendar className="h-4 w-4 text-green-600" />
                    Publié le {formatDateTime(post.published_at)}
                  </span>
                  {post.author && (
                    <span className="flex items-center gap-1.5" data-testid="article-author">
                      <User className="h-4 w-4 text-orange-500" />
                      par {post.author}
                    </span>
                  )}
                  <span className="flex items-center gap-1.5" data-testid="article-views">
                    <Eye className="h-4 w-4 text-gray-400" />
                    {post.views || 0} vue{(post.views || 0) > 1 ? 's' : ''}
                  </span>
                </div>

                {post.pdfUrl && (
                  <div className="mb-6">
                    <a
                      href={post.pdfUrl}
                      download
                      className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors"
                    >
                      <Download className="h-4 w-4" />
                      Télécharger le PDF
                    </a>
                  </div>
                )}

                <div
                  className="prose prose-lg max-w-none"
                  dangerouslySetInnerHTML={{ __html: post.content }}
                />

                <div className="mt-8 pt-6 border-t border-gray-100">
                  <ShareButtons url={currentUrl} title={post.title} />
                </div>
              </CardContent>
            </Card>

            {/* 3 dernières actualités */}
            {relatedPosts.length > 0 && (
              <div className="mt-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Dernières actualités</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {relatedPosts.map((relatedPost) => (
                    <Card key={relatedPost.id} className="group hover:shadow-lg transition-shadow bg-white flex flex-col">
                      {relatedPost.image && (
                        <div className="relative h-40 overflow-hidden rounded-t-lg">
                          <img
                            src={relatedPost.image}
                            alt={relatedPost.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                        </div>
                      )}
                      <CardContent className="p-4 flex flex-col flex-1">
                        <span className="text-xs text-gray-500 mb-1">{formatDateTime(relatedPost.published_at)}</span>
                        <h3 className="font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors line-clamp-2">
                          {relatedPost.title}
                        </h3>
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2 flex-1">{relatedPost.excerpt}</p>
                        <Link to={`/actualites/${relatedPost.slug}`} className="text-green-600 hover:text-green-700 text-sm font-medium">
                          Lire plus →
                        </Link>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};
